import {
    FileText,
    LayoutGrid,
    List as ListIcon,
    Edit,
    Trash2,
} from 'lucide-react'
import ContractAttachment from '@/components/ContractAttachment'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createColumns,
    createDateFormatter,
    useAttachmentExpandable,
} from '@/components/ui/data-table-customized'
import type {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import DownIcons from '@/icons/DownIcons'
import EditIcons from '@/icons/EditIcons'
import UpIcons from '@/icons/UpIcons'
import { UpdateSubcontractModalProps } from '@/modals/ContractModals/UpdateSubcontractModal'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { formatDateWithFormatter, randomAvatarSrc } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Link } from 'react-router-dom'
import { Avatar, Chip, IconButton, Title, Typogaphy } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'
import { deleteSubcontract, getSubcontracts } from '../../services'
import { Subcontract } from '../../types'
import ContractButtons from '@/modules/Contract/shared/Buttons'
import { CustomerContract } from '@/modules/Contract/types'

interface IListProps {
    contractType?: 'customer' | 'partner'
    contractID: number
    contract?: CustomerContract
}

interface SubcontractCardProps {
    subcontract: Subcontract
    contractType: 'customer' | 'partner'
    contractID: number
    onEdit: () => void
    onDelete: () => void
}

const SubcontractCard: React.FC<SubcontractCardProps> = ({
    subcontract,
    contractType,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)

    const subcontractStatus = useMemo(
        () =>
            CONTRACT_STATUS(t)?.find(
                (status) => status?.value === subcontract?.subcontract_status
            ),
        [subcontract]
    )

    const isExpired = useMemo(() => {
        if (!subcontract?.subcontract_expiry_date) return false
        return new Date(subcontract.subcontract_expiry_date) < new Date()
    }, [subcontract])

    return (
        <div className="w-full bg-white rounded-md shadow-sm border border-gray-100 overflow-hidden mb-4">
            <div
                className={`bg-gradient-to-r ${isExpired ? 'from-red-50 to-orange-50' : 'from-indigo-50 to-blue-50'} border-b border-gray-100`}
            >
                <div className="flex justify-between items-center p-6">
                    <div className="flex items-center gap-4 flex-1">
                        <div
                            className={`w-2 h-2 rounded-full ${isExpired ? 'bg-red-500' : 'bg-indigo-500'} ${!isExpired ? 'animate-pulse' : ''}`}
                        ></div>
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                                {contractType === 'customer' ? (
                                    <Link
                                        to={generatePathDatacentral(
                                            paths.subContractDetail,
                                            {
                                                contractID:
                                                    subcontract?.contract_id,
                                                subcontractID:
                                                    subcontract?.subcontract_id,
                                            }
                                        )}
                                    >
                                        <Typogaphy className="font-medium text-primary hover:underline">
                                            [{subcontract?.subcontract_code}]
                                        </Typogaphy>
                                    </Link>
                                ) : (
                                    <Typogaphy className="font-medium text-gray-800">
                                        [{subcontract?.subcontract_code}]
                                    </Typogaphy>
                                )}
                            </div>
                            <Typogaphy
                                className={`font-medium ${isExpired ? 'text-error' : 'text-primary'}`}
                            >
                                {subcontract?.subcontract_name || '---'}
                            </Typogaphy>
                            <div className="flex items-center gap-4 text-gray-600">
                                <Typogaphy size="small">
                                    {t('effective-date')}:{' '}
                                    {subcontract?.subcontract_effective_date
                                        ? formatDateWithFormatter(
                                              subcontract?.subcontract_effective_date,
                                              'DD/MM/YYYY'
                                          )
                                        : '---'}
                                </Typogaphy>
                                <Typogaphy
                                    size="small"
                                    className={
                                        isExpired
                                            ? 'text-red-600 font-medium'
                                            : 'text-orange-600 font-medium'
                                    }
                                >
                                    {t('expiry-date')}:{' '}
                                    {subcontract?.subcontract_expiry_date
                                        ? formatDateWithFormatter(
                                              subcontract?.subcontract_expiry_date,
                                              'DD/MM/YYYY'
                                          )
                                        : '---'}
                                </Typogaphy>
                                {subcontract?.employee_name && (
                                    <div className="flex items-center gap-2">
                                        <Avatar
                                            src={randomAvatarSrc(
                                                subcontract.employee_name
                                            )}
                                            size="small"
                                            className="w-5 h-5"
                                        />
                                        <Typogaphy
                                            size="small"
                                            className="text-gray-600"
                                        >
                                            {subcontract.employee_name}
                                        </Typogaphy>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Status chips moved to right corner */}
                        <div className="flex gap-2">
                            <Chip
                                label={
                                    subcontractStatus?.label ||
                                    subcontract?.subcontract_status
                                }
                                color={
                                    (subcontractStatus?.color as Color) ||
                                    'primary'
                                }
                                className="px-3 font-medium"
                                size="small"
                            />
                            {isExpired && (
                                <Chip
                                    label={t('expired')}
                                    color="error"
                                    className="px-3 font-medium"
                                    size="small"
                                />
                            )}
                        </div>

                        {/* Optimized action buttons */}
                        <div className="flex items-center gap-1">
                            <IconButton
                                size="small"
                                onClick={onEdit}
                                className="text-primary hover:bg-indigo-50 transition-colors"
                            >
                                <EditIcons className="w-4 h-4" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={onDelete}
                                className="text-c-error hover:bg-red-50 transition-colors"
                            >
                                <DeleteOutlineIcons className="w-4 h-4" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                className="text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                {expanded ? <UpIcons /> : <DownIcons />}
                            </IconButton>
                        </div>
                    </div>
                </div>
            </div>

            {expanded && (
                <div className="p-4">
                    <div className="grid xs:grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <Typogaphy className="text-gray-500 text-sm mb-2">
                                {t('subcontract_code')}
                            </Typogaphy>
                            <Typogaphy className="font-medium">
                                {subcontract?.subcontract_code}
                            </Typogaphy>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                            <Typogaphy className="text-gray-500 text-sm mb-2">
                                {t('subcontract_times')}
                            </Typogaphy>
                            <Typogaphy className="font-medium">
                                {subcontract?.subcontract_effective_date
                                    ? formatDateWithFormatter(
                                          subcontract?.subcontract_effective_date,
                                          'DD/MM/YYYY'
                                      )
                                    : '---'}
                                {' - '}
                                {subcontract?.subcontract_expiry_date
                                    ? formatDateWithFormatter(
                                          subcontract?.subcontract_expiry_date,
                                          'DD/MM/YYYY'
                                      )
                                    : '---'}
                            </Typogaphy>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                    {subcontract?.employee_name ? (
                                        <Avatar
                                            src={randomAvatarSrc(
                                                subcontract.employee_name
                                            )}
                                            size="small"
                                        />
                                    ) : (
                                        <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                            👤
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Typogaphy className="text-gray-500 text-sm">
                                        {t('employee')}
                                    </Typogaphy>
                                    <Typogaphy className="font-medium">
                                        {subcontract?.employee_name ||
                                            t('no-employee-assigned')}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <Typogaphy className="text-gray-500 text-sm mb-2">
                                {t('subcontract_register_date')}
                            </Typogaphy>
                            <Typogaphy className="font-medium">
                                {subcontract?.subcontract_register_date
                                    ? formatDateWithFormatter(
                                          subcontract?.subcontract_register_date,
                                          'DD/MM/YYYY'
                                      )
                                    : '---'}
                            </Typogaphy>
                        </div>
                        {(subcontract?.services?.length ?? 0) > 0 && (
                            <div className="col-span-2 bg-gray-50 rounded-lg p-4 w-full">
                                <Typogaphy className="text-gray-500 text-sm mb-2">
                                    {t('services')}
                                </Typogaphy>
                                <div className="flex flex-col gap-1">
                                    {subcontract?.services?.map((service) => (
                                        <Link
                                            key={service.service_id}
                                            to={generatePathDatacentral(
                                                paths.serviceDetail,
                                                { id: service.service_id }
                                            )}
                                        >
                                            <Typogaphy className="font-medium text-primary">
                                                {[
                                                    service.service_id,
                                                    service.service_name,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' - ')}
                                            </Typogaphy>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                        <Typogaphy className="text-gray-500 text-sm mb-2">
                            {t('note')}
                        </Typogaphy>
                        <Typogaphy className="text-blue-700">
                            {subcontract?.subcontract_note}
                        </Typogaphy>
                    </div>
                    {subcontract?.attachments &&
                        subcontract?.attachments?.length > 0 && (
                            <div className="mt-4">
                                <ContractAttachment
                                    attachments={subcontract.attachments}
                                />
                            </div>
                        )}
                </div>
            )}
        </div>
    )
}

type ViewMode = 'table' | 'card'

const ListComponent: React.FC<IListProps> = ({
    contractType = 'customer',
    contractID = 1,
    contract,
}) => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const [viewMode, setViewMode] = useState<ViewMode>('table')

    const attachmentExpandable = useAttachmentExpandable<Subcontract>({
        getAttachments: (row) =>
            row.attachments?.filter((a) => !a.attachment_closed) || [],
    })

    const { data, isLoading } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SUBCONTRACTS, contractType, contractID],
            queryFn: () => getSubcontracts({ contractID, contractType }),
        },
        { showLoading: false, showError: false }
    )

    const { refetch } = useQueryClientCallback({
        pattern: 'subcontracts',
        mode: 'contains',
    })

    const { mutateAsync: deleteMutation } = useMutationWithHandler(
        { mutationFn: deleteSubcontract },
        {
            successMessage: t('contract:delete-subcontract-success'),
            reactQueryKeys: QUERY_KEYS.SUBCONTRACTS,
            onSuccess: () => refetch(),
        }
    )

    // Sort subcontracts by status priority: pending -> active/completed -> cancelled
    const sortedSubcontracts = useMemo(() => {
        if (!data?.results) return []

        const statusPriority = {
            pending: 1,
            active: 2,
            completed: 2,
            cancelled: 3,
            canceled: 3,
        }

        return [...data.results].sort((a, b) => {
            const priorityA =
                statusPriority[
                    a.subcontract_status as keyof typeof statusPriority
                ] || 2
            const priorityB =
                statusPriority[
                    b.subcontract_status as keyof typeof statusPriority
                ] || 2

            if (priorityA !== priorityB) return priorityA - priorityB

            return (
                new Date(b.subcontract_effective_date || '').getTime() -
                new Date(a.subcontract_effective_date || '').getTime()
            )
        })
    }, [data?.results])

    const handleEdit = (subcontract: Subcontract) => {
        if (contractType === 'customer') {
            setModal<UpdateSubcontractModalProps>(
                'ContractModals/UpdateSubcontractModal/index',
                {
                    contractID: contractID,
                    subcontractID: subcontract?.subcontract_id,
                    subcontract: subcontract,
                }
            )
        } else {
            setModal('ContractModals/CreatePartnerSubcontractModal/index', {
                contractID: contractID,
                subcontract: subcontract,
            })
        }
    }

    const handleDelete = (subcontract: Subcontract) => {
        setConfirmation({
            open: true,
            title: t('confirm'),
            description: t('delete-subcontract-warning'),
            useReason: true,
            onConfirm: (reason) =>
                deleteMutation({
                    contractType,
                    contractID: subcontract?.contract_id,
                    subcontractID: subcontract?.subcontract_id,
                    reason: reason as string,
                }),
        })
    }

    // DataTableCustomized columns
    const tableColumns: DataTableColumn<Subcontract>[] = createColumns([
        {
            key: 'subcontract_code',
            title: t('subcontract_code'),
            width: 250,
            pinned: 'left',
            cellClassName: 'truncate',
            render: (value, row) =>
                contractType === 'customer' ? (
                    <Link
                        to={generatePathDatacentral(paths.subContractDetail, {
                            contractID: row.contract_id,
                            subcontractID: row.subcontract_id,
                        })}
                        className="text-primary hover:underline truncate"
                    >
                        {value as string}
                    </Link>
                ) : (
                    <Typogaphy className="truncate text-xs">
                        {value as string}
                    </Typogaphy>
                ),
        },
        {
            key: 'subcontract_name',
            title: t('subcontract_name'),
            width: 250,
            render: (value, row) => {
                const isExpired = row.subcontract_expiry_date
                    ? new Date(row.subcontract_expiry_date) < new Date()
                    : false
                return (
                    <span className={`${isExpired ? 'text-destructive' : ''}`}>
                        {(value as string) || '---'}
                    </span>
                )
            },
        },
        {
            key: 'subcontract_effective_date',
            title: t('effective-date'),
            width: 140,
            cellClassName: 'text-right',
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'subcontract_expiry_date',
            title: t('expiry-date'),
            width: 140,
            cellClassName: 'text-right',
            render: (value) => {
                const isExpired = value
                    ? new Date(value as string) < new Date()
                    : false
                return (
                    <span
                        className={isExpired ? 'text-c-error font-medium' : ''}
                    >
                        {value
                            ? formatDateWithFormatter(
                                  value as string,
                                  'DD/MM/YYYY'
                              )
                            : '---'}
                    </span>
                )
            },
        },
        {
            key: 'employee_name',
            title: t('employee'),
            width: 180,
        },
        {
            key: 'subcontract_status',
            title: t('status'),
            width: 180,
            render: (value, row) => {
                const status = CONTRACT_STATUS(t)?.find(
                    (s) => s?.value === value
                )
                const isExpired = row.subcontract_expiry_date
                    ? new Date(row.subcontract_expiry_date) < new Date()
                    : false
                return (
                    <div className="flex gap-1 flex-wrap">
                        <Chip
                            label={status?.label || (value as string)}
                            color={(status?.color as Color) || 'primary'}
                            size="small"
                            variant="contained"
                        />
                        {isExpired && (
                            <Chip
                                label={t('expired')}
                                color="error"
                                size="small"
                            />
                        )}
                    </div>
                )
            },
        },
        {
            key: 'service_name',
            title: t('services'),
            width: 250,
            render: (_, row) => {
                // subcontracts.service_id is legacy and no query ever writes it (always -1).
                // services[] comes from the subcontract_services junction, which is the truth.
                const services = row.services ?? []
                if (services.length === 0)
                    return <span className="text-gray-400">---</span>
                return (
                    <div className="flex flex-col gap-0.5">
                        {services.map((service) => (
                            <Link
                                key={service.service_id}
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    { id: service.service_id }
                                )}
                                className="text-primary hover:underline"
                            >
                                {[service.service_id, service.service_name]
                                    .filter(Boolean)
                                    .join(' - ')}
                            </Link>
                        ))}
                    </div>
                )
            },
        },
        {
            key: 'attachments',
            title: t('attachments'),
            width: 100,
            render: (_, row) => {
                const atts = row.attachments?.filter(
                    (a) => !a.attachment_closed
                )
                if (!atts?.length)
                    return <span className="text-gray-400">---</span>
                return (
                    <span className="flex items-center gap-1 text-muted-foreground">
                        <FileText className="w-4 h-4" />
                        <span>{atts.length}</span>
                    </span>
                )
            },
        },
        {
            key: 'subcontract_note',
            title: t('note'),
            width: 200,
            render: (value) =>
                value ? (
                    <span className="text-muted-foreground italic truncate">
                        {value as string}
                    </span>
                ) : (
                    <span className="text-gray-400">---</span>
                ),
        },
    ])

    const tableActions: DataTableAction<Subcontract>[] = [
        {
            key: 'edit',
            label: t('edit'),
            icon: <Edit className="w-4 h-4" />,
            onClick: (row) => handleEdit(row),
        },
        {
            key: 'delete',
            label: t('delete'),
            icon: <Trash2 className="w-4 h-4" />,
            variant: 'danger',
            onClick: (row) => handleDelete(row),
        },
    ]

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Title title={t('subcontract-list')} />
                {[1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="w-full bg-gray-100 rounded-xl h-24 animate-pulse"
                    ></div>
                ))}
            </div>
        )
    }

    return (
        <Paper className="space-y-2 mt-2">
            {/* Header with view toggle */}
            <div className="flex items-center justify-end">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-1.5 rounded transition-colors ${viewMode === 'table' ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            <ListIcon className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('card')}
                            className={`p-1.5 rounded transition-colors ${viewMode === 'card' ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                    </div>
                    {/* Customer contracts get one entry point holding both ways to make an annex;
                        partner contracts have no convert flow, so they keep the plain button. */}
                    {contract &&
                        (contractType === 'customer' ? (
                            <ContractButtons.SubcontractAddMenu
                                contract={contract}
                                contractID={contractID}
                            />
                        ) : (
                            <ContractButtons.CreateSubcontract
                                contract={contract}
                                contractID={contractID}
                            />
                        ))}
                </div>
            </div>

            {viewMode === 'table' ? (
                <DataTableCustomized<Subcontract>
                    columns={tableColumns}
                    rows={sortedSubcontracts}
                    rowKey={(row) => String(row.subcontract_id)}
                    size="small"
                    resizable
                    actions={tableActions}
                    emptyMessage={t('no-subcontracts-found')}
                    expandable={attachmentExpandable}
                    toolbar={{
                        settings: {
                            show: false,
                            storageKey: 'subcontract-list',
                        },
                    }}
                />
            ) : sortedSubcontracts?.length === 0 ? (
                <div className="text-center py-12 px-4">
                    <Typogaphy className="text-gray-500">
                        {t('no-subcontracts-found')}
                    </Typogaphy>
                </div>
            ) : (
                <div className="space-y-2 px-4 pb-4">
                    {sortedSubcontracts?.map((subcontract) => (
                        <SubcontractCard
                            key={subcontract.subcontract_id}
                            subcontract={subcontract}
                            contractType={contractType}
                            contractID={contractID}
                            onEdit={() => handleEdit(subcontract)}
                            onDelete={() => handleDelete(subcontract)}
                        />
                    ))}
                </div>
            )}
        </Paper>
    )
}

const List = (props: IListProps) => {
    return <ListComponent {...props} />
}
export default List
